import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import moment from 'moment'
import { SVGInjector } from '@tanem/svg-injector'
import queryString from 'query-string'
import PropTypes from 'prop-types'
import { Button } from '../../components'
import Settings from './Settings'
import { homeScreenLinkBody, homeScreenLinkHeader } from './HomeScreenLinkModal'
import { eulaBody, eulaFooter, eulaHeader } from '../containers/EulaModal'
import FeedbackModal from './FeedbackModal'
import { uploadBody, uploadFooter, uploadHeader } from './UploadModal'
import { getPimBaseUrl } from '../../utils/config'
import { useBootstrapModal, useLogicButton } from '../../utils/useForm'
import { simulateClick } from '../../utils/utils'
import { downloadAction } from '../../utils/actions'
import { fieldUpdateSettingsAction } from '../utils/slices'
import { RenderIf } from '../../utils/fns'
import { GlobalReset } from '../../containers/TopNavigation/components'
import Modal from '../components/Modal'
import { useWindowSize } from '../../utils/hooks'
import CloudDownloadTwoToneIcon from '@mui/icons-material/CloudDownloadTwoTone';
import CloudUploadTwoToneIcon from '@mui/icons-material/CloudUploadTwoTone';
import SettingsTwoToneIcon from '@mui/icons-material/SettingsTwoTone';
import AddToHomeScreenTwoToneIcon from '@mui/icons-material/AddToHomeScreenTwoTone';
import SchoolTwoToneIcon from '@mui/icons-material/SchoolTwoTone';

const propTypes = {
  branding: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
}

const TopNav = (props) => {
  const {about: About, print: Print, downloadProps } = props

  const params = queryString.parse(window.location.search) ?? {}

  const dispatch = useDispatch()

  const inputFreeze = useSelector((state) => state.calculation.input.inputFreeze)
  const mobileNav = useSelector((state) => state.settings.mobileNav)
  const { appCode, appName, logoUrl, gaEventName, downloadFileName, appVersion, navigationConfig } = props.appConfig

  const uploadModal = useBootstrapModal(false)
  const settingsModal = useBootstrapModal(false)
  const feedbackModal = useBootstrapModal(false)
  const homeScreenLinkModal = useBootstrapModal(false)

  const openEULA = localStorage.getItem('sst_blue_print_eula_accepted') !== 'true'
  const eulaModal = useBootstrapModal(openEULA)

  const { width } = useWindowSize()

  const onChangeCB = (name, value) => {
    dispatch(
      fieldUpdateSettingsAction({
        [name]: value,
      }),
    )
  }
  const mobileNavButton = useLogicButton('mobileNav', mobileNav, onChangeCB)

  useEffect(() => {
    const mySVGsToInject = document.querySelectorAll('img.injectSVG')
    SVGInjector(mySVGsToInject)

    if (localStorage.getItem('sst_blue_print_eula_accepted') === 'true') {
      eulaModal.closeModal()
    }
  }, [])

  const handleTutorialClick = (e) => {
    const tutorialTriggerElement = document.querySelector('div.inmplayer-trigger')
    simulateClick(tutorialTriggerElement)
  }

  useEffect(() => {
    if (mobileNav) document.documentElement.classList.add('-mobileNavOpen')
    else document.documentElement.classList.remove('-mobileNavOpen')
  }, [mobileNav])

  useEffect(() => {
    if (width >= 768) {
      dispatch(
        fieldUpdateSettingsAction({
          mobileNav: false,
        }),
      )
    }
  }, [width])

  return (
    <>
      <header className="topNav">
        <a
          className="sstLogoLink"
          style={params && params.source === 'EC' ? { pointerEvents: 'none', cursor: 'none' } : {}}
          href="https://www.strongtie.com/"
        >
          <img
            id="sstLogo"
            className="injectSVG"
            data-src="assets/images/sst-logo-revised-noborder.svg"
            alt="sstLogo"
          />
        </a>

        <div className="topNav__buttonGroup">
          <Button
            {...downloadProps}
            variant="topNav"
            className="button topNav__button -download"
            onClick={() => dispatch(downloadAction())}
            data-testid="download"
          >
            <CloudDownloadTwoToneIcon className='material-icons-two-tone' />
            <span className="button__text">Download</span>
          </Button>

          <Button
            variant="topNav"
            className="button topNav__button -upload"
            disabled={inputFreeze}
            onClick={uploadModal.openModal}
            data-testid="upload"
          >
            <CloudUploadTwoToneIcon className='material-icons-two-tone' />
            <span className="button__text">Upload</span>
          </Button>

          <GlobalReset />

          {RenderIf(Print, <Print />)}

          {RenderIf(About, <About />)}

          <Button
            variant="topNav"
            className="button topNav__button -settings"
            onClick={settingsModal.openModal}
            data-testid="settings"
          >
            <SettingsTwoToneIcon className='material-icons-two-tone' />
            <span className="button__text">Settings</span>
          </Button>

          <FeedbackModal appCode={appCode} appVersion={appVersion} downloadFileName={downloadFileName}/>

          {navigationConfig && navigationConfig.showTutorial ? (
            <Button
              variant="topNav"
              className="button topNav__button -tutorials"
              onClick={handleTutorialClick}
              data-testid="tutorials"
            >
              <SchoolTwoToneIcon className='material-icons-two-tone' />
              <span className="button__text">Tutorials</span>
            </Button>
          ) : null}

          <Button
            variant="topNav"
            className="button topNav__button -homeScreen"
            onClick={homeScreenLinkModal.openModal}
            data-testid="homeScreen"
          >
            <AddToHomeScreenTwoToneIcon className='material-icons-two-tone material-icons-two-tone--noinvert' />
            <span className="button__text">Add to Home Screen</span>
          </Button>
        </div>

        <Modal
          className="upload"
          show={uploadModal.value}
          onHide={uploadModal.closeModal}
          header={uploadHeader}
          body={uploadBody({ uploadModal: uploadModal, appConfig: props.appConfig })}
          footer={uploadFooter({ uploadModal: uploadModal })}
          data-testid="uploadModal"
        ></Modal>


        <Modal
          className="settings"
          show={settingsModal.value}
          onHide={settingsModal.closeModal}
          header={<h3>Settings</h3>}
          body={<Settings appCode={appCode} />}
          data-testid="settingsModal"
        ></Modal>

        <Modal
          className="-eula"
          show={eulaModal.value}
          onHide={() => {
            eulaModal.closeModal()
            setTimeout(() => {
              window.location.assign(getPimBaseUrl(window.location.hostname))
            }, 0)
          }}
          hideclosebutton={'true'}
          header={eulaHeader({ appName: appName })}
          body={eulaBody}
          footer={eulaFooter({ eulaModal: eulaModal })}
          data-testid="eulaModal"
        ></Modal>

 

        <Modal
          className="-homeScreenLink"
          show={homeScreenLinkModal.value}
          onHide={homeScreenLinkModal.closeModal}
          header={homeScreenLinkHeader({ appName: appName })}
          body={homeScreenLinkBody}
          data-testid="homeScreenLinkModal"
        ></Modal>

        {props.branding}

        <button className="mobileNavigation" {...mobileNavButton} aria-label="Menu" data-testid="mobileNavigation">
          <span className="material-icons mobileNavigation__menu">menu</span>
          <span className="material-icons mobileNavigation__close">close</span>
        </button>

        <div className="dateStamp">
          Results generated on {moment().format('dddd, MMMM D, YYYY')}
        </div>
      </header>

      <div className="headerShadow"></div>
    </>
  )
}

TopNav.propTypes = propTypes

export default TopNav
